import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
    ActivityInterface,
    AdvisoryModel,
    ApiClientInterface,
    CityInterface,
    ClientInterface,
    ClientNotificationInterface,
    ClientSignatureType,
    ContractDataInterface,
    ContractInterface,
    ContractLiteInterface,
    CountryInterface,
    CreatedContractInterface,
    DictionaryInterface,
    DisinvestmentInterface,
    DocumentInterface,
    DocumentType,
    FeeInterface,
    FileInterface,
    FullClientInterface,
    IdInterface,
    ModelInterface,
    OptimalSelectedProductInterface,
    OrderClientInterface,
    OrderDocumentInterface,
    OrderSignedInterface,
    OrderTypeAvailableInterface,
    ProductInterface,
    ProfessionalPositionInterface,
    OtherProfessionalPositionInterface,
    SelectedProductInterface,
    StreetTypeInterface,
    TestInterface,
    TestResultInterface,
    TraderInterface,
    TransferInterface,
    UserInterface,
    ValidationClient,
    ValidModelInterface,
    RoleNameInterface,
} from '../../../core';
import { ClientApiClient } from './client-api-client.service';
import { ContractApiClient } from './contract-api-client.service';
import { DocumentApiClient } from './document-api-client.service';
import { LegalApiClient } from './legal-api-client.service';
import { OrderApiClient } from './order-api-client.service';
import { ProductApiClient } from './product-api-client.service';
import { TaxApiClient } from './tax-api-client.service';
import { TestApiClient } from './test-api-client.service';
import { TransferApiClient } from './transfer-api-client.service';
import { UserApiClient } from './user-api-client.service';
import { ProductsByOrdertype } from '../../domain/data/productsbyordertype';
import { ProductsbyOrderTypeInterface } from 'src/app/core/domain/data/productsbyordertype.interface';

@Injectable()
export class ApiClient implements ApiClientInterface {
    constructor(
        private clientApiClient: ClientApiClient,
        private contractApiClient: ContractApiClient,
        private documentApiClient: DocumentApiClient,
        private legalApiClient: LegalApiClient,
        private orderApiClient: OrderApiClient,
        private productApiClient: ProductApiClient,
        private taxApiClient: TaxApiClient,
        private transferApiClient: TransferApiClient,
        private testApiClient: TestApiClient,
        private userApiClient: UserApiClient
    ) {}

    acceptOrderSignatures(selectedProductId: IdInterface): Observable<boolean> {
        return this.orderApiClient.acceptOrderSignatures(selectedProductId);
    }

    acceptTestSignSignatures(clientCode: IdInterface): Observable<boolean> {
        return this.testApiClient.acceptTestSignSignatures(clientCode);
    }

    addSelectedProducts(contractCode: IdInterface, products: ProductInterface[]): Observable<SelectedProductInterface[]> {
        return this.productApiClient.addSelectedProducts(contractCode, products);
    }

    addOrderSelectedProduct(product: SelectedProductInterface): Observable<OrderSignedInterface> {
        return this.orderApiClient.addOrderSelectedProduct(product);
    }

    checkClient(client: ClientInterface, captcha: string): Observable<boolean> {
        return this.clientApiClient.checkClient(client, captcha);
    }

    checkEmail(email: string, clientCode?: IdInterface): Observable<boolean> {
        return this.clientApiClient.checkEmail(email, clientCode);
    }

    checkPhone(phone: string, clientCode?: IdInterface): Observable<boolean> {
        return this.clientApiClient.checkPhone(phone, clientCode);
    }

    checkSelectedProducts(contractCode: IdInterface, products: SelectedProductInterface[]): Observable<SelectedProductInterface[]> {
        return this.productApiClient.checkSelectedProducts(contractCode, products);
    }

    continueOrderElectronicSignature(product: SelectedProductInterface, clientCode: IdInterface): Observable<OrderSignedInterface> {
        return this.orderApiClient.continueOrderElectronicSignature(product, clientCode);
    }

    continueTestElectronicSignature(clientCode: IdInterface): Observable<OrderSignedInterface> {
        return this.testApiClient.continueTestElectronicSignature(clientCode);
    }

    createContract(contract: ContractInterface): Observable<ContractInterface> {
        return this.contractApiClient.createContract(contract);
    }

    createContractWithProducts(
        contract: ContractInterface,
        products: ProductInterface[],
        captcha: string
    ): Observable<CreatedContractInterface> {
        return this.contractApiClient.createContractWithProducts(contract, products, captcha);
    }

    evaluateFee(contractCode: IdInterface, fee: FeeInterface): Observable<number> {
        return this.transferApiClient.evaluateFee(contractCode, fee);
    }

    finalizeContract(contractCode: IdInterface): Observable<boolean> {
        return this.contractApiClient.finalizeContract(contractCode);
    }

    cancelContract(contractCode: IdInterface): Observable<boolean> {
        return this.contractApiClient.cancelContract(contractCode);
    }

    getActivities(): Observable<ActivityInterface[]> {
        return this.userApiClient.getActivities();
    }

    getAdvisoryModels(): Observable<AdvisoryModel[]> {
        return this.userApiClient.getAdvisoryModels();
    }

    getCities(countryId: IdInterface): Observable<CityInterface[]> {
        return this.userApiClient.getCities(countryId);
    }

    getClientByDocumentId(docType: DocumentType, docId: string): Observable<ClientInterface> {
        return this.clientApiClient.getClientByDocumentId(docType, docId);
    }

    getContract(): Observable<ContractInterface> {
        return this.contractApiClient.getContract();
    }

    getContracts(clientCode: IdInterface): Observable<ContractLiteInterface[]> {
        return this.contractApiClient.getContracts(clientCode);
    }

    getContractData(contractCode: IdInterface): Observable<ContractDataInterface> {
        return this.contractApiClient.getContractData(contractCode);
    }

    getContractDocuments(contractCode: IdInterface): Observable<DocumentInterface[]> {
        return this.documentApiClient.getDocuments('Contract', contractCode);
    }

    getContractSignatures(contractCode: IdInterface, clientCode?: IdInterface): Observable<any> {
        return this.contractApiClient.getContractSignatures(contractCode, clientCode);
    }

    getContractSignedDocuments(contractCode: IdInterface): Observable<OrderDocumentInterface[]> {
        return this.documentApiClient.getContractSignedDocuments(contractCode);
    }

    getCountries(): Observable<CountryInterface[]> {
        return this.userApiClient.getCountries();
    }

    getDictionary(): Observable<DictionaryInterface[]> {
        return this.userApiClient.getDictionary();
    }

    getDocumentDetail(document: DocumentInterface): Observable<any> {
        return this.documentApiClient.getDocumentDetail(document);
    }

    getExternalTraders(): Observable<TraderInterface[]> {
        return this.userApiClient.getExternalTraders();
    }

    getFullClient(clientCode: IdInterface, contractCode?: IdInterface): Observable<FullClientInterface> {
        return this.clientApiClient.getFullClient(clientCode, contractCode);
    }

    getClientDocuments(clientCode: IdInterface, contractCode?: IdInterface): Observable<DocumentInterface[]> {
        return this.documentApiClient.getDocuments('Client', contractCode, clientCode);
    }

    getLegalDisclaimer(): Observable<string> {
        return this.legalApiClient.getLegalDisclaimer();
    }

    getModel(id: IdInterface): Observable<ModelInterface> {
        return this.transferApiClient.getModel(id);
    }

    getModelPdf(id: IdInterface): Observable<FileInterface> {
        return this.transferApiClient.getModelPdf(id);
    }

    getMyProducts(contractCode: IdInterface): Observable<ProductInterface[]> {
        return this.productApiClient.getMyProducts(contractCode);
    }

    getNormativeCountries(): Observable<CountryInterface[]> {
        return this.userApiClient.getNormativeCountries();
    }

    getOptimalSelectedProduct(
        contractCode: IdInterface,
        productCode: IdInterface,
        amount: number
    ): Observable<OptimalSelectedProductInterface> {
        return this.productApiClient.getOptimalSelectedProduct(contractCode, productCode, amount);
    }

    getOrderDocuments(selectedProductId: IdInterface): Observable<any> {
        return this.documentApiClient.getOrderDocuments(selectedProductId);
    }

    getOrderTypes(
        contractCode: IdInterface,
        product: SelectedProductInterface,
        clientCode?: IdInterface
    ): Observable<OrderTypeAvailableInterface[]> {
        return this.orderApiClient.getOrderTypes(contractCode, product, clientCode);
    }

    getProduct(productCode: IdInterface): Observable<ProductInterface> {
        return this.productApiClient.getProduct(productCode);
    }

    getProductDocument(productCode: IdInterface): Observable<string> {
        return this.documentApiClient.getProductDocument(productCode);
    }

    getProducts(): Observable<ProductInterface[]> {
        return this.productApiClient.getProducts();
    }

    getProfessionalPositions(): Observable<ProfessionalPositionInterface[]> {
        return this.userApiClient.getProfessionalPositions();
    }
    getOtherProfessionalPositions(): Observable<OtherProfessionalPositionInterface[]> {
        return this.userApiClient.getOtherProfessionalPositions();
    }
    getProfile(): Observable<UserInterface> {
        return this.userApiClient.getProfile();
    }

    getSelectedProducts(contractCode: IdInterface, clientCode?: IdInterface): Observable<SelectedProductInterface[]> {
        return this.productApiClient.getSelectedProducts(contractCode, clientCode);
    }

    getSelectedProductsSignatories(product: SelectedProductInterface, clientCode: IdInterface): Observable<OrderClientInterface[]> {
        return this.productApiClient.getSelectedProductsSignatories(product, clientCode);
    }

    getStreetTypes(): Observable<StreetTypeInterface[]> {
        return this.userApiClient.getStreetTypes();
    }

    getRoleName(): Observable<RoleNameInterface[]> {
        return this.userApiClient.getRoleName();
    }

    getTaxClassifications(classification: string, isLegalPerson: boolean): Observable<string[]> {
        return this.taxApiClient.getClassifications(classification, isLegalPerson);
    }

    getTest(clientId: IdInterface, advisoryModel: AdvisoryModel): Observable<TestInterface> {
        return this.testApiClient.getTest(clientId, advisoryModel);
    }

    getTestResults(clientId: IdInterface, advisoryModel: AdvisoryModel): Observable<TestResultInterface> {
        return this.testApiClient.getTestResults(clientId, advisoryModel);
    }

    getTestSignDocuments(clientCode: IdInterface): Observable<any> {
        return this.documentApiClient.getTestSignDocuments(clientCode);
    }

    getTraders(): Observable<TraderInterface[]> {
        return this.userApiClient.getTraders();
    }

    getValidateClientNotifications(): Observable<ClientNotificationInterface[]> {
        return this.clientApiClient.getValidateClientNotifications();
    }

    getValidModels(contract: ContractInterface, client: ClientInterface): Observable<ValidModelInterface[]> {
        return this.transferApiClient.getValidModels(contract, client);
    }

    rejectClient(contractCode: IdInterface, clientCode: IdInterface, message: string): Observable<boolean> {
        return this.clientApiClient.rejectClient(contractCode, clientCode, message);
    }

    rejectContract(contractCode: IdInterface, message: string): Observable<boolean> {
        return this.contractApiClient.rejectContract(contractCode, message);
    }

    removeDocument(document: DocumentInterface): Observable<boolean> {
        return this.documentApiClient.removeDocument(document);
    }

    removeOrderSelectedProduct(product: SelectedProductInterface): Observable<boolean> {
        return this.orderApiClient.removeOrderSelectedProduct(product);
    }

    removeSelectedProduct(product: SelectedProductInterface): Observable<boolean> {
        return this.productApiClient.removeSelectedProduct(product);
    }

    rememberOrderElectronicSignature(product: SelectedProductInterface, clientCode: IdInterface): Observable<boolean> {
        return this.orderApiClient.rememberOrderElectronicSignature(product, clientCode);
    }

    reviseClient(contractCode: IdInterface, clientCode: IdInterface, message: string): Observable<boolean> {
        return this.clientApiClient.reviseClient(contractCode, clientCode, message);
    }

    reviseContract(contractCode: IdInterface, message: string): Observable<boolean> {
        return this.contractApiClient.reviseContract(contractCode, message);
    }

    setTransfer(transfer: TransferInterface): Observable<boolean> {
        return this.transferApiClient.setTransfer(transfer);
    }

    setFee(contractCode: IdInterface, fee: FeeInterface): Observable<boolean> {
        return this.transferApiClient.setFee(contractCode, fee);
    }

    signDisinvestment(
        contractCode: IdInterface,
        signatories: OrderClientInterface[],
        disinvestment: DisinvestmentInterface
    ): Observable<boolean> {
        return this.transferApiClient.signDisinvestment(contractCode, signatories, disinvestment);
    }

    signModel(contractCode: IdInterface, signatories: OrderClientInterface[], modelId: IdInterface): Observable<boolean> {
        return this.transferApiClient.signModel(contractCode, signatories, modelId);
    }

    signTest(clientCode: IdInterface, signatureType: ClientSignatureType): Observable<boolean> {
        return this.testApiClient.signTest(clientCode, signatureType);
    }

    signTransfer(contractCode: IdInterface, signatories: OrderClientInterface[], transfer: TransferInterface): Observable<boolean> {
        return this.transferApiClient.signTransfer(contractCode, signatories, transfer);
    }

    updateContract(contract: ContractInterface): Observable<ContractInterface> {
        return this.contractApiClient.updateContract(contract);
    }

    updateFullClient(client: FullClientInterface): Observable<boolean> {
        return this.clientApiClient.updateFullClient(client);
    }

    updateOrderSelectedProduct(product: SelectedProductInterface): Observable<OrderSignedInterface> {
        return this.orderApiClient.updateOrderSelectedProduct(product);
    }

    updateSelectedProducts(contractCode: IdInterface, products: SelectedProductInterface[]): Observable<boolean> {
        return this.productApiClient.updateSelectedProducts(contractCode, products);
    }

    updateTest(clientId: IdInterface, test: TestInterface, requiredSignature: boolean): Observable<TestResultInterface> {
        return this.testApiClient.updateTest(clientId, test, requiredSignature);
    }

    uploadClientDocument(clientCode: IdInterface, document: DocumentInterface): Observable<boolean> {
        return this.documentApiClient.uploadDocument(String(clientCode), 'Client', document);
    }

    uploadContractDocument(contractCode: IdInterface, document: DocumentInterface): Observable<boolean> {
        return this.documentApiClient.uploadDocument(String(contractCode), 'Contract', document);
    }

    uploadSignedOrderDocument(selectedProductId: IdInterface, document: File): Observable<boolean> {
        return this.documentApiClient.uploadSignedOrderDocument(selectedProductId, document);
    }

    uploadTestSignDocument(clientCode: IdInterface, document: File): Observable<boolean> {
        return this.documentApiClient.uploadTestSignDocument(clientCode, document);
    }

    validateClient(clientCode: IdInterface): Observable<boolean> {
        return this.clientApiClient.validateClient(clientCode);
    }

    validateClientData(clientCode: IdInterface, validations: ValidationClient[]): Observable<boolean> {
        return this.clientApiClient.validateClientData(clientCode, validations);
    }

    validateClientDocument(clientCode: IdInterface, documentId: IdInterface): Observable<boolean> {
        return this.documentApiClient.validateDocument(String(clientCode), 'Client', String(documentId));
    }

    validateContract(contractCode: IdInterface): Observable<boolean> {
        return this.contractApiClient.validateContract(contractCode);
    }

    validateContractDocument(contractCode: IdInterface, documentId: IdInterface): Observable<boolean> {
        return this.documentApiClient.validateDocument(String(contractCode), 'Contract', String(documentId));
    }

    validateNotification(contractCode: IdInterface): Observable<boolean> {
        return this.contractApiClient.validateNotification(contractCode);
    }




    getProductsByOrderType(portfolioCode: IdInterface, clientCode?: IdInterface, distributionChannel?: string[], orderType?: string , riskLevel?: string): Observable<ProductInterface[]> {
        return this.productApiClient.getProductsByOrderType(portfolioCode, clientCode, distributionChannel, orderType , riskLevel);
    }


    getProductsByOrderTypeClient(portfolioCode: IdInterface, clientCode?: IdInterface, distributionChannel?: string[], orderType?: string , riskLevel?: string): Observable<ProductInterface[]> {
        return this.productApiClient.getProductsByOrderTypeClient(portfolioCode, clientCode, distributionChannel, orderType , riskLevel);
    }
}
